"use client";

import type * as React from "react";
import { Box } from "../../components/ui/box";
import { Sheet, SheetPopup } from "../../components/ui/sheet";
import { useAppShell } from "./app-shell-context";
import type { Omittable } from "../../lib/props";

/**
 * The application shell: a full-width site header pinned to the top, and below
 * it a row of three columns — a fixed module rail, a collapsible/resizable
 * module-menu pane, and the content area rendered as an inset card.
 *
 * On desktop the header spans the whole width (workspace switcher at the left,
 * search centered, account menu at the right); nothing sits above it. The rail
 * is always visible; collapsing (`open=false`) clips the pane to zero and
 * slides it away, leaving just the rail. On mobile there is no pinned header at
 * all — the header, rail, and pane all live inside a slide-in drawer toggled by
 * {@link AppShellTrigger}, which page-level headers render, so the content area
 * keeps the full viewport height.
 *
 * The consuming app owns the pane content (rail tiles, module menus) and the
 * persisted widths — this component is domain-free layout only.
 */
export type AppShellProps = Omittable<{
  /** Full-width bar pinned to the top of the shell. */
  siteHeader?: React.ReactNode;
  /** Fixed narrow far-left navigation column. */
  moduleRail?: React.ReactNode;
  /** Width of the module rail column in px. Defaults to 66. */
  moduleRailWidth?: number;
  /**
   * Content of the module-menu pane (right of the rail). No chrome is added.
   *
   * The pane's width — and the drag seam that changes it — belong to this
   * content, not to the shell: the pane's root Box carries `resize="right"`
   * and drives `setSidebarWidth` from `useAppShell()`. The shell only reads the
   * resulting width to size the column.
   */
  sidebar?: React.ReactNode;
  /** Main content — rendered inside the inset card and stretched to fill. */
  children: React.ReactNode;
  className?: string;
}>;

const PANE_TRANSITION = "transition-[width] duration-200 ease-linear";

export function AppShell({
  siteHeader,
  moduleRail,
  moduleRailWidth = 66,
  sidebar,
  children,
  className,
}: AppShellProps): React.ReactElement {
  const {
    isMobile,
    open,
    openMobile,
    setOpenMobile,
    sidebarWidth,
    isResizingSidebar,
  } = useAppShell();

  const paneVisible = open && !!sidebar;
  const hasMobileNav = !!moduleRail || !!sidebar || !!siteHeader;

  const header = siteHeader ? (
    // Positioned + z-index so header-anchored overlays that expand in-flow
    // (the global-search panel) paint over page content below.
    <Box position="relative" shrink={false} zIndex={40}>
      {siteHeader}
    </Box>
  ) : null;

  const content = (
    <ContentPane
      inset={!isMobile}
      // The rail's right gutter already spaces the card when no pane shows, and
      // a visible pane sits flush — so the card never needs its own left pad
      // when either column exists.
      flushLeft={!!moduleRail || paneVisible}
      // Square the card's left corners against a flush pane so the shared seam
      // stays a straight line.
      squareLeft={paneVisible}
    >
      {children}
    </ContentPane>
  );

  if (isMobile) {
    return (
      <Box background="sunken" className={className} direction="column" fill>
        <Box direction="column" fill overflow="hidden" position="relative">
          {content}
        </Box>
        {hasMobileNav && (
          <Sheet onOpenChange={setOpenMobile} open={openMobile}>
            <SheetPopup
              className="w-[86dvw] max-w-none"
              // A nav drawer opening must not pull focus onto the first rail
              // tile; focus rests on the popup and the user tabs in.
              initialFocus={false}
              showCloseButton={false}
              side="left"
              // Rail and pane each carry their own rounded `sidebar` surface —
              // a slab behind them would just be a third, flatter one.
              variant="bare"
            >
              {/* No `overflow="hidden"` here: the header below deliberately
                  overflows the drawer to span the viewport. */}
              <Box direction="column" fill gap="sm">
                {/* The site header rides inside the drawer, so the workspace
                    switcher / search / account controls present with the
                    navigation instead of spending a pinned header row. It
                    spans the FULL viewport even though the drawer is 86dvw —
                    it overflows to the right, over the backdrop — so the
                    controls stretch edge to edge of the screen. */}
                {siteHeader && (
                  <Box direction="column" shrink={false} width="100dvw">
                    {siteHeader}
                  </Box>
                )}
                <Box
                  direction="row"
                  fill
                  gap="sm"
                  overflow="hidden"
                  padding="sm"
                  // With a header above, the column gap already supplies the
                  // same 8px the rail and pane keep between themselves.
                  paddingTop={siteHeader ? "none" : "sm"}
                >
                  {moduleRail && (
                    <Box
                      direction="column"
                      shrink={false}
                      width={moduleRailWidth}
                    >
                      {moduleRail}
                    </Box>
                  )}
                  {sidebar && (
                    <Box direction="column" grow minWidth={0}>
                      {sidebar}
                    </Box>
                  )}
                </Box>
              </Box>
            </SheetPopup>
          </Sheet>
        )}
      </Box>
    );
  }

  return (
    <Box background="sunken" className={className} direction="column" fill>
      {header}
      <Box direction="row" fill overflow="hidden" position="relative">
        {moduleRail && (
          <Box
            direction="column"
            marginX="sm"
            height="100%"
            paddingBottom="sm"
            position="relative"
            shrink={false}
            width={moduleRailWidth}
            zIndex={10}
          >
            {moduleRail}
          </Box>
        )}
        {sidebar && (
          // Outer clips; inner keeps its full width so collapsing slides the
          // pane out from beneath the clip rather than reflowing it.
          <Box
            // No easing while a seam is dragging — the pane must track the
            // pointer exactly, not ease toward each streamed width.
            className={isResizingSidebar ? undefined : PANE_TRANSITION}
            height="100%"
            overflow="hidden"
            position="relative"
            // The pane is a fixed-width column, not a shrinkable one. Without
            // this it shares the row's overflow with the content column: a page
            // whose min-content width exceeds the row (a wide board or table)
            // squeezes the pane down and its own content spills into a
            // horizontal scrollbar. `shrink={false}` pins the pane and leaves
            // the content column — which has `minWidth={0}` and clips inside —
            // to absorb the overflow, exactly like the rail beside it.
            shrink={false}
            width={paneVisible ? sidebarWidth : 0}
            zIndex={10}
          >
            <Box
              direction="column"
              height="100%"
              paddingBottom="sm"
              shrink={false}
              width={sidebarWidth}
            >
              {sidebar}
            </Box>
          </Box>
        )}
        <Box direction="column" grow height="100%" minWidth={0}>
          {content}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Content column. On desktop it renders as an inset card (bordered, rounded,
 * page-background) flush against the pane; on mobile it fills flush. Children
 * are wrapped in a filling Box so a page's own `fill` resolves against a
 * definite height.
 */
function ContentPane({
  inset,
  children,
  flushLeft,
  squareLeft,
}: Omittable<{
  inset?: boolean;
  children: React.ReactNode;
  flushLeft?: boolean;
  squareLeft?: boolean;
}>): React.ReactElement {
  return (
    <Box
      direction="column"
      fill
      // No top padding: rail and pane start at the row top, so the card's top
      // edge lines up with theirs. Bottom padding matches their 8px inset.
      paddingBottom={inset ? "sm" : undefined}
      paddingLeft={inset && !flushLeft ? "sm" : undefined}
      paddingRight={inset ? "sm" : undefined}
    >
      <Box overflow="hidden"
        // The stable anchor overlays pin to: a sidebar-style sheet insets
        // itself to this card's rect (top/right/bottom + radius) so it reads
        // as a panel INSIDE the content card, not a full-viewport layer.
        id="__dialog-anchor"
        // `background` — the content column IS the page. The gutter around it
        // and the navigation either side of it are the `sunken` frame, and
        // everything the column hosts stacks upward from here: panels are
        // `card`, overlays are `popover`.
        //
        // `border` supplies the WIDTH; the surface supplies the color. A
        // surface emits `border-<name>-border`, which is a border-color
        // utility only — on its own it paints nothing, because the default
        // border-style is `none`. `--background-shadow` is a soft drop with no
        // ring layer, so without this the card has no edge at all against the
        // sunken gutter.
        border={inset}
        surface={inset ? "background" : undefined}
        direction="column"
        fill
        rounded={inset ? "md" : undefined}
        // Against a flush pane only the outer corners round, so the shared seam
        // stays a straight line.
        roundedSide={inset && squareLeft ? "right" : undefined}
      >
        {children}
      </Box>
    </Box>
  );
}
